import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { SearchGroup } from '../../../../components/index-page/SearchGroup';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { Select } from '../../../../components/inputs/Select';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { useAuth } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../useRecruitmentUsers';
import {
  emptyJobPostingDraft,
  isJobPostingDraftComplete,
  sampleJobPostingDraft,
  toJobPostingRequest,
  type JobPostingDraft,
} from '../utils/jobPostingDraft';
import { sampleJobPostingTags } from '../utils/sampleJobPostingTags';
import { vacanciesApi } from '../vacanciesApi';
import { vacancyGroupKey, type VacancyOption } from '../vacancyTypes';
import { JobPostingFields } from './JobPostingFields';

/**
 * Tag vacant plantilla items for job posting — the Vacancies module's Create.
 *
 * **The POSITION first** — the catalog title, ONE entry per title whatever units it is posted in
 * (product call, 15 and 16 Oct 2026). **Then two panels side by side, each with its own search and
 * its own scroll** (16 Oct 2026): LEFT, every unposted vacant item of that position, ticked to
 * choose; RIGHT, what is chosen, where an item is removed. Only UNPOSTED items are offered —
 * removing a posting stays its own action.
 *
 * The container-then-member pair: both panels are drawn always and say why they are empty, and
 * changing the position CLEARS the selection — a choice belongs to the position it was made under.
 *
 * **One request per item**, in order: the route names the item, so each tag lands in that item's
 * History Logs. A refusal stops the run and keeps the form open with what is left.
 *
 * **The Job Posting stage rides on every tag** (19 Oct 2026) — Posted by, Posted at, Remarks, asked
 * once below the panels and written onto each item chosen. Posted by is PICKED; it opens on the
 * signed-in account and today, so the form opens answerable.
 */
export function TagForJobPostingModal({
  open,
  initialItemId,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** The row menu opens this form on one item: its position is chosen and the item selected. */
  initialItemId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const currentUserId = useAuth().user?.id ?? null;
  const accounts = useRecruitmentUsers();

  const [posting, setPosting] = useState<JobPostingDraft>(() => emptyJobPostingDraft(currentUserId));
  const [options, setOptions] = useState<VacancyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [ticked, setTicked] = useState<string[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    vacanciesApi
      .options(controller.signal)
      .then((loaded) => {
        setOptions(loaded);
        setLoading(false);

        // Opened from a row: land on that item's position, with the item already selected.
        const seed = initialItemId ? loaded.find((o) => o.id === initialItemId) : undefined;
        if (seed) {
          setGroupKey(vacancyGroupKey(seed));
          setTicked([seed.id]);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoading(false);
        setLoadError(caught instanceof ApiError ? caught.message : 'The vacant items could not be loaded.');
      });

    return () => controller.abort();
  }, [initialItemId]);

  /** One picker row per catalog position, carrying how many of its items are still unposted. */
  const positions = useMemo(() => {
    const byKey = new Map<string, { option: VacancyOption; count: number }>();

    for (const option of options) {
      const key = vacancyGroupKey(option);
      const entry = byKey.get(key);
      if (entry) entry.count += 1;
      else byKey.set(key, { option, count: 1 });
    }

    return [...byKey.entries()].map(([key, { option, count }]) => ({
      value: key,
      label: option.positionTitle,
      sublabel: `${option.positionCode ?? 'No catalog code'} · ${count} vacant`,
      keywords: option.positionCode ?? undefined,
    }));
  }, [options]);

  const items = groupKey === null ? [] : options.filter((o) => vacancyGroupKey(o) === groupKey);
  const selected = items.filter((i) => ticked.includes(i.id));

  /*
    The searches narrow what is DRAWN, never what is chosen: a tick on an item the left search is
    hiding stays a tick, and the right panel's count is always the whole selection.
  */
  const leftShown = items.filter((i) => matches(i, leftSearch));
  const rightShown = selected.filter((i) => matches(i, rightSearch));
  const allShownTicked = leftShown.length > 0 && leftShown.every((i) => ticked.includes(i.id));
  const someShownTicked = leftShown.some((i) => ticked.includes(i.id));

  const canSave = ticked.length > 0 && isJobPostingDraftComplete(posting) && confirmed && !saving;

  function toggle(id: string, on: boolean) {
    setTicked((current) => (on ? (current.includes(id) ? current : [...current, id]) : current.filter((x) => x !== id)));
  }

  /** Ticks or unticks the rows the LEFT search is showing — never the ones it is hiding. */
  function toggleShown(on: boolean) {
    const shownIds = leftShown.map((i) => i.id);
    setTicked((current) =>
      on ? [...current, ...shownIds.filter((id) => !current.includes(id))] : current.filter((id) => !shownIds.includes(id)),
    );
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const posted: string[] = [];
    const request = toJobPostingRequest(posting);

    try {
      for (const id of ticked) {
        await vacanciesApi.tag(id, request);
        posted.push(id);
      }

      toast.success(`Tagged ${posted.length} item${posted.length === 1 ? '' : 's'} for job posting.`);
      onSaved();
    } catch (caught) {
      // What landed stays landed: it is no longer unposted, so it leaves both panels.
      setOptions((current) => current.filter((o) => !posted.includes(o.id)));
      setTicked((current) => current.filter((id) => !posted.includes(id)));
      const failure = caught instanceof ApiError ? caught : new ApiError('The items could not be tagged.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'tag-job-posting' });

      if (posted.length > 0) {
        toast.success(`Tagged ${posted.length} item${posted.length === 1 ? '' : 's'} for job posting.`);
      }
    } finally {
      setSaving(false);
    }
  }

  const noPosition = groupKey === null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Tag for job posting"
      size="2xl"
      id="tag-job-posting-modal"
      autogenerate={{
        onGenerate: () => {
          setPosting(sampleJobPostingDraft(accounts.users));
          const sample = sampleJobPostingTags(options);
          if (!sample) return;
          setGroupKey(sample.groupKey);
          setTicked(sample.itemIds);
        },
        disabled: saving || options.length === 0 || accounts.loading,
      }}
      confirm={{
        id: 'tag-job-posting-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="tag-job-posting-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="tag-job-posting-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loadError ? (
          <p className="text-xs text-slate-500">{loadError}</p>
        ) : !loading && options.length === 0 ? (
          <p className="text-xs text-slate-500">Every vacant item is already tagged for job posting.</p>
        ) : (
          <>
            <Select
              id="tag-job-posting-position"
              label="Position"
              required
              placeholder="Choose a position…"
              value={groupKey}
              onChange={(value) => {
                setGroupKey(value || null);
                // A choice belongs to the position it was made under.
                setTicked([]);
                setLeftSearch('');
                setRightSearch('');
              }}
              options={positions}
              loading={loading}
              disabled={saving}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              {/* LEFT — every unposted vacant item of the position, ticked to choose. */}
              <section aria-labelledby="tag-job-posting-vacant-title" className="flex min-w-0 flex-col gap-2">
                <h3 id="tag-job-posting-vacant-title" className="text-sm font-medium text-slate-800">
                  Vacant items
                </h3>
                <SearchGroup
                  id="tag-job-posting-vacant"
                  value={leftSearch}
                  onChange={setLeftSearch}
                  placeholder="Search item numbers or offices"
                  total={noPosition ? undefined : leftShown.length}
                />
                <div className="modern-scrollbar h-64 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200">
                  <table className="w-full table-fixed">
                    <thead className="sticky top-0 z-10 bg-slate-100">
                      <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                        <th className="w-10 px-3 py-2 text-left">
                          <Checkbox
                            id="tag-job-posting-all"
                            aria-label="Select every item shown"
                            checked={allShownTicked}
                            indeterminate={someShownTicked && !allShownTicked}
                            disabled={saving || leftShown.length === 0}
                            onChange={(e) => toggleShown(e.target.checked)}
                          />
                        </th>
                        <th className="border-l border-slate-200 px-3 py-2 text-left">Item no.</th>
                        <th className="border-l border-slate-200 px-3 py-2 text-left">Office assignment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leftShown.length === 0 ? (
                        <EmptyRow colSpan={3}>
                          {noPosition
                            ? 'Choose a position to list its vacant items.'
                            : 'No vacant item matches that search.'}
                        </EmptyRow>
                      ) : (
                        leftShown.map((item) => (
                          <tr key={item.id} className="border-t border-slate-200 align-middle hover:bg-slate-50">
                            <td className="px-3 py-2">
                              <Checkbox
                                id={`tag-job-posting-item-${item.id}`}
                                aria-label={`Select ${item.itemNumber}`}
                                checked={ticked.includes(item.id)}
                                disabled={saving}
                                onChange={(e) => toggle(item.id, e.target.checked)}
                              />
                            </td>
                            <td className="truncate border-l border-slate-200 px-3 py-2">
                              <label htmlFor={`tag-job-posting-item-${item.id}`} className="font-mono text-sm text-slate-600">
                                {item.itemNumber}
                              </label>
                            </td>
                            <td className="truncate border-l border-slate-200 px-3 py-2 text-sm text-slate-600">
                              {item.orgUnitName}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* RIGHT — what is chosen; an item comes off with its Remove. */}
              <section aria-labelledby="tag-job-posting-selected-title" className="flex min-w-0 flex-col gap-2">
                <h3 id="tag-job-posting-selected-title" className="text-sm font-medium text-slate-800">
                  Selected for job posting
                </h3>
                <SearchGroup
                  id="tag-job-posting-selected"
                  value={rightSearch}
                  onChange={setRightSearch}
                  placeholder="Search selected items"
                  total={noPosition ? undefined : selected.length}
                />
                <div className="modern-scrollbar h-64 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200">
                  <table className="w-full table-fixed">
                    <thead className="sticky top-0 z-10 bg-slate-100">
                      <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                        <th className="px-3 py-2 text-left">Item no.</th>
                        <th className="border-l border-slate-200 px-3 py-2 text-left">Office assignment</th>
                        <th className="w-20 border-l border-slate-200 px-3 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rightShown.length === 0 ? (
                        <EmptyRow colSpan={3}>
                          {selected.length === 0
                            ? 'Nothing selected yet. Tick items on the left.'
                            : 'No selected item matches that search.'}
                        </EmptyRow>
                      ) : (
                        rightShown.map((item) => (
                          <tr key={item.id} className="border-t border-slate-200 align-middle hover:bg-slate-50">
                            <td className="truncate px-3 py-2 font-mono text-sm text-slate-600">{item.itemNumber}</td>
                            <td className="truncate border-l border-slate-200 px-3 py-2 text-sm text-slate-600">
                              {item.orgUnitName}
                            </td>
                            <td className="border-l border-slate-200 px-3 py-2 text-right">
                              <Tooltip label="Remove">
                                <button
                                  type="button"
                                  aria-label={`Remove ${item.itemNumber}`}
                                  onClick={() => toggle(item.id, false)}
                                  disabled={saving}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            {/* The Job Posting stage — asked once, written onto every item chosen. */}
            <JobPostingFields
              idPrefix="tag-job-posting"
              draft={posting}
              onChange={setPosting}
              users={accounts.users}
              usersLoading={accounts.loading}
              usersError={accounts.error}
              disabled={saving}
              error={error}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

/** One line saying why a panel has nothing in it — never an empty grid. */
function EmptyRow({ colSpan, children }: { colSpan: number; children: string }) {
  return (
    <tr className="border-t border-slate-200 first:border-t-0">
      <td colSpan={colSpan} className="px-3 py-3 text-xs text-slate-500">
        {children}
      </td>
    </tr>
  );
}

/** A search over what the reader can see in a row: the item number and its office. */
function matches(item: VacancyOption, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return item.itemNumber.toLowerCase().includes(needle) || item.orgUnitName.toLowerCase().includes(needle);
}
